<template>
    <div v-if="hasProjects">
        <nav
            class="flex border rounded-lg py-2 max-w-2xl h-full hover:text-sky-500 focus-within:text-sky-500"
            :class="[active ? 'border-sky-400 text-sky-600' : 'text-taupe-500']"
            aria-label="Project and Environment"
        >
            <ol class="flex items-center space-x-1 h-full">
                <li class="flex items-center h-full">
                    <router-link
                        :to="{
                            name: 'ProjectFlagsScreen',
                            params: current,
                        }"
                        class="flex items-center"
                        aria-current="page"
                    >
                        <span class="ml-4 text-sm font-medium">{{
                            currentProject?.name
                        }}</span>
                        <zen-icon-chevron-right
                            css-classes="h-5 w-5 shrink-0"
                        />
                        <span
                            class="ml-2 text-sm font-medium flex items-center"
                        >
                            <span
                                v-if="currentEnvironment?.color"
                                aria-hidden="true"
                                class="inline-block h-3 w-3 rounded-xs mr-1"
                                :style="{
                                    backgroundColor: currentEnvironment.color,
                                }"
                            ></span>
                            {{
                                currentEnvironment
                                    ? currentEnvironment.name
                                    : ''
                            }}
                        </span>
                    </router-link>
                </li>
                <li class="px-2 h-full flex items-center">
                    <button
                        type="button"
                        ref="referenceRef"
                        @click="toggleMenu"
                        class="cursor-pointer text-sm font-medium rounded-sm p-1 hover:text-sky-600 hover:bg-sky-200"
                        id="menu-environment-button"
                        :aria-expanded="isOpen"
                        aria-haspopup="dialog"
                        aria-label="Switch project or environment"
                    >
                        <zen-icon-chevron-down
                            v-if="!isOpen"
                            css-classes="h-4 w-4"
                            stroke-width="2"
                        />
                        <zen-icon-chevron-up
                            v-if="isOpen"
                            css-classes="h-4 w-4"
                            stroke-width="2"
                        />
                    </button>
                </li>
            </ol>
        </nav>

        <Teleport to="body">
            <transition
                enter-active-class="transition ease-out duration-100"
                enter-class="transform opacity-0 scale-95"
                enter-to-class="transform opacity-100 scale-100"
                leave-active-class="transition ease-in duration-75"
                leave-class="transform opacity-100 scale-100"
                leave-to-class="transform opacity-0 scale-95"
            >
                <div
                    v-if="isOpen"
                    ref="floatingRef"
                    :style="floatingStyles"
                    class="z-100 md:w-128 md:flex rounded-md drop-shadow-xl bg-white ring-1 ring-black/20 focus:outline-hidden"
                    role="dialog"
                    aria-labelledby="menu-environment-button"
                    tabindex="-1"
                >
                    <div class="w-full divide-y">
                        <div class="px-2 py-1.5 flex bg-taupe-50 rounded-t-md">
                            <div
                                class="text-xs uppercase text-taupe-500 align-text-bottom pt-1.5 pr-2"
                            >
                                {{ currentProject?.name }} ({{
                                    currentEnvironment?.key
                                }}) Actions:
                            </div>
                            <favorite-button
                                :project="currentProject"
                                :environment="currentEnvironment"
                            />
                            <environment-keys-button
                                :project="currentProject"
                                :environment="currentEnvironment"
                            />
                            <swap-button
                                v-if="showSwapButton"
                                :disabled-reason="swapBlockedReason"
                                @click="openSwap"
                            />
                            <div class="grow text-right">
                                <button
                                    type="button"
                                    @click="closeMenu"
                                    aria-label="Close"
                                    class="cursor-pointer p-1 text-taupe-500 hover:text-taupe-800 hover:bg-taupe-200 rounded-full"
                                >
                                    <zen-icon-close css-classes="h-5 w-5" />
                                </button>
                            </div>
                        </div>
                        <div class="flex divide-x">
                            <div class="w-3/5 px-3 py-2 pb-3 space-y-3">
                                <div
                                    class="rounded-md border border-taupe-200 bg-white"
                                    data-testid="environment-picker-selection-list"
                                >
                                    <div
                                        class="border-b border-taupe-100 px-2.5 py-2"
                                    >
                                        <div
                                            id="environment-picker-project-label"
                                            class="text-xs font-medium uppercase tracking-[0.08em] text-taupe-500"
                                        >
                                            Project
                                        </div>

                                        <div
                                            v-if="!isSingleProject"
                                            class="mt-1.5"
                                        >
                                            <select
                                                v-model="future.projectKey"
                                                @change="updateEnvironmentKey"
                                                data-testid="environment-picker-project-select"
                                                aria-labelledby="environment-picker-project-label"
                                                class="block w-full rounded-md border-taupe-300 py-1.5 pl-2.5 pr-9 text-sm focus:border-sky-500 focus:outline-hidden focus:ring-sky-500 text-taupe-600"
                                            >
                                                <option
                                                    v-for="project in projects"
                                                    :key="project.key"
                                                    :value="project.key"
                                                >
                                                    {{ project.name }} ({{
                                                        project.key
                                                    }})
                                                </option>
                                            </select>
                                        </div>
                                        <div
                                            v-if="isSingleProject"
                                            class="mt-1.5"
                                        >
                                            <div
                                                class="block w-full rounded-md border border-taupe-200 bg-taupe-50 px-2.5 py-1.5 text-sm text-taupe-600"
                                            >
                                                {{ projects[0].name }} ({{
                                                    projects[0].key
                                                }})
                                            </div>
                                        </div>
                                    </div>

                                    <div class="px-2.5 py-2">
                                        <div
                                            id="environment-picker-environment-label"
                                            class="text-xs font-medium uppercase tracking-[0.08em] text-taupe-500"
                                        >
                                            Environment
                                        </div>

                                        <div
                                            v-if="!isSingleEnvironment"
                                            class="mt-1.5"
                                        >
                                            <select
                                                v-model="future.environmentKey"
                                                data-testid="environment-picker-environment-select"
                                                aria-labelledby="environment-picker-environment-label"
                                                class="block w-full rounded-md border-taupe-300 py-1.5 pl-2.5 pr-9 text-sm focus:border-sky-500 focus:outline-hidden focus:ring-sky-500 text-taupe-600"
                                            >
                                                <option
                                                    v-for="environment in environments"
                                                    :key="environment.key"
                                                    :value="environment.key"
                                                >
                                                    {{ environment.name }} ({{
                                                        environment.key
                                                    }})
                                                </option>
                                            </select>
                                        </div>
                                        <div
                                            v-if="isSingleEnvironment"
                                            class="mt-1.5"
                                        >
                                            <div
                                                class="block w-full rounded-md border border-taupe-200 bg-taupe-50 px-2.5 py-1.5 text-sm text-taupe-600"
                                            >
                                                {{ environments[0].name }} ({{
                                                    environments[0].key
                                                }})
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="text-right space-x-2">
                                    <zen-button
                                        variant="primary"
                                        @click="set()"
                                    >
                                        Switch
                                    </zen-button>
                                </div>
                            </div>
                            <div class="w-2/5 px-3 py-2 space-y-1">
                                <div class="flex items-center justify-between">
                                    <div
                                        class="text-sm text-taupe-500 uppercase"
                                    >
                                        Favorites
                                    </div>
                                    <div
                                        class="rounded-sm border border-taupe-200 bg-taupe-50 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-[0.08em] text-taupe-500"
                                    >
                                        {{ favorites.length }}
                                    </div>
                                </div>
                                <div
                                    v-if="favorites.length === 0"
                                    class="rounded-md border border-dashed border-taupe-200 bg-taupe-50 px-3 py-4 text-sm text-taupe-400"
                                    data-testid="environment-picker-favorites-empty"
                                >
                                    No favorites yet.
                                </div>
                                <div
                                    v-else
                                    class="h-40 overflow-y-auto rounded-md border border-taupe-200 bg-white"
                                    data-testid="environment-picker-favorites-list"
                                >
                                    <button
                                        @click="selectFavorite(favorite)"
                                        v-for="favorite in favorites"
                                        :key="favorite.ulid"
                                        class="cursor-pointer block w-full border-b border-taupe-100 px-2 py-1.5 text-left text-sky-700 hover:bg-sky-50 hover:text-sky-800 last:border-b-0"
                                        data-testid="environment-picker-favorite-item"
                                    >
                                        <div
                                            class="flex items-center justify-between gap-2"
                                        >
                                            <span
                                                class="truncate text-sm font-medium leading-5"
                                                >{{
                                                    favorite.project_name
                                                }}</span
                                            >
                                            <span
                                                class="shrink-0 rounded-sm border border-sky-200 bg-sky-50 px-1 py-0.5 text-[10px] font-medium uppercase tracking-[0.08em] text-sky-700"
                                            >
                                                {{
                                                    favorite.environment_key.toUpperCase()
                                                }}
                                            </span>
                                        </div>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </transition>
        </Teleport>

        <!-- Stays in the app tree, not teleported to body: the selects inside it portal into the app root, so they must share its stacking. -->
        <create-swap-slide-in
            v-if="canSwapIntoCurrent"
            :is-open="swap.modal.show"
            :environments="swapEnvironments"
            :initial-target-key="swap.modal.initialTargetKey"
            :diff="swap.modal.diff"
            :diff-loading="swap.modal.diffLoading"
            :processing="swap.modal.processing"
            @close="swap.close()"
            @preview="onSwapPreview"
            @confirm="onSwapConfirm"
        />
    </div>
</template>

<script>
/* Buttons */
import FavoriteButton from './buttons/FavoriteButton.vue'
import EnvironmentKeysButton from './buttons/EnvironmentKeysButton.vue'
import SwapButton from './buttons/SwapButton.vue'
import CreateSwapSlideIn from '@/screens/trusted/admin/modals/CreateSwapSlideIn.vue'

import { useSessionStore } from '@/stores/session'
import { useFloatingMenu } from '@/composables/useFloatingMenu'
import { useFocusTrap } from '@/composables/useFocusTrap'
import { useSwapFlow } from '@/composables/useSwapFlow'
import { usePermissions, PERMISSIONS } from '@/composables/usePermissions'
import { useFeatureFlag } from '@/utils/feature-flags'

export default {
    components: {
        FavoriteButton,
        EnvironmentKeysButton,
        SwapButton,
        CreateSwapSlideIn,
    },
    props: {
        active: {
            type: Boolean,
            required: true,
        },
    },
    setup() {
        const {
            referenceRef,
            floatingRef,
            isOpen,
            floatingStyles,
            close,
            toggle,
        } = useFloatingMenu({
            placement: 'bottom-end',
            offsetPx: 4,
        })

        useFocusTrap(floatingRef, isOpen)

        const { can } = usePermissions()
        // Temporary swaps ship dark: the swap action stays hidden until the flag is on.
        const { enabled: swapsEnabled } = useFeatureFlag(
            'enable-temporary-swaps',
            false
        )

        return {
            referenceRef,
            floatingRef,
            isOpen,
            floatingStyles,
            closeMenu: close,
            toggleMenu: toggle,
            can,
            swapsEnabled,
            swap: useSwapFlow(),
        }
    },
    computed: {
        sessionStore() {
            return useSessionStore()
        },
        // The swap action shows whenever swaps are on, even where a swap can't
        // land, so it can say why instead of leaving the user hunting for it.
        showSwapButton() {
            return Boolean(
                this.swapsEnabled &&
                this.currentEnvironment &&
                this.currentProject
            )
        },
        // A swap lands on the current environment, so it needs an environment that
        // allows overlays and isn't protected, and environment admin rights.
        // Null means a swap can land here.
        swapBlockedReason() {
            const environment = this.currentEnvironment
            const project = this.currentProject
            if (!environment || !project) {
                return null
            }

            if (environment.confirmation_required === true) {
                return "You can't swap into this environment because it requires confirmation to change flag values."
            }

            if (environment.can_overlay !== true) {
                return "You can't swap into this environment. Temporary flag overlays are turned off in its settings."
            }

            const allowed = this.can(PERMISSIONS.ENVIRONMENT_UPDATE, {
                projectUlid: project.ulid,
                environmentUlid: environment.ulid,
            })

            return allowed
                ? null
                : "You can't swap into this environment. You need permission to update it."
        },
        canSwapIntoCurrent() {
            return this.showSwapButton && this.swapBlockedReason === null
        },
        swapEnvironments() {
            return this.currentProject?.environments?.data ?? []
        },
        grantScopes() {
            return (
                this.sessionStore.data?.user?.data?.grant_scopes ?? {
                    account: true,
                    project_ulids: [],
                    environment_ulids: [],
                }
            )
        },
        current() {
            if (this.sessionStore.data === null) {
                return []
            }

            return this.sessionStore.data.current
        },
        currentEnvironment() {
            const environmentKey = this.current.environmentKey
            if (!this.currentProject) return undefined

            const match = this.currentProject.environments.data.find(
                (environment) =>
                    environment.key.toLowerCase() ===
                    environmentKey.toLowerCase()
            )

            return match ?? this.currentProject.environments.data[0]
        },
        currentProject() {
            const projectKey = this.current.projectKey
            const match = this.accessibleProjects.find(
                (project) =>
                    project.key.toLowerCase() === projectKey.toLowerCase()
            )

            return match ?? this.accessibleProjects[0]
        },
        accessibleProjects() {
            const allProjects = this.sessionStore.data?.projects?.data
            if (allProjects == null) {
                return []
            }

            if (this.grantScopes.account) {
                return allProjects
            }

            const projectUlids = this.grantScopes.project_ulids ?? []
            const environmentUlids = this.grantScopes.environment_ulids ?? []

            return allProjects
                .map((project) => {
                    if (projectUlids.includes(project.ulid)) {
                        return project
                    }

                    const grantedEnvs = (
                        project.environments?.data ?? []
                    ).filter((env) => environmentUlids.includes(env.ulid))

                    if (grantedEnvs.length > 0) {
                        return {
                            ...project,
                            environments: { data: grantedEnvs },
                        }
                    }

                    return null
                })
                .filter((project) => project !== null)
        },
        favorites() {
            const allFavorites = [
                ...this.sessionStore.data.user.data.favorites.data,
            ]

            if (!this.grantScopes.account) {
                const accessibleKeys = new Set()
                for (const project of this.accessibleProjects) {
                    for (const env of project.environments?.data ?? []) {
                        accessibleKeys.add(project.key + ':' + env.key)
                    }
                }

                return allFavorites
                    .filter((fav) =>
                        accessibleKeys.has(
                            fav.project_key + ':' + fav.environment_key
                        )
                    )
                    .sort((a, b) => {
                        if (a.project_name !== b.project_name) {
                            return a.project_name > b.project_name ? 1 : -1
                        } else if (a.project_name === b.project_name) {
                            return a.environment_key > b.environment_key
                                ? 1
                                : -1
                        } else {
                            return 0
                        }
                    })
            }

            return allFavorites.sort((a, b) => {
                if (a.project_name !== b.project_name) {
                    return a.project_name > b.project_name ? 1 : -1
                } else if (a.project_name === b.project_name) {
                    return a.environment_key > b.environment_key ? 1 : -1
                } else {
                    return 0
                }
            })
        },
        futureProject() {
            const projectKey = this.future.projectKey
            return this.accessibleProjects.find(
                (project) =>
                    project.key.toLowerCase() === projectKey.toLowerCase()
            )
        },
        projects() {
            return this.accessibleProjects.filter(
                (project) =>
                    project.environments.data != null &&
                    project.environments.data.length > 0
            )
        },
        environments() {
            if (this.future.projectKey == null) return []
            const project = this.accessibleProjects.filter(
                (project) => project.key === this.future.projectKey
            )[0]
            return project ? project.environments.data : []
        },
        hasProjects() {
            return this.projects.length > 0
        },
        hasRecents() {
            return this.recents.length > 0
        },
        isSingleProject() {
            return this.projects.length == 1
        },
        isSingleEnvironment() {
            return this.isOpen
                ? this.futureProject?.environments.data.length == 1
                : this.currentProject?.environments.data.length == 1
        },
        recents() {
            const allRecents = this.sessionStore.data.user.data.recents.data

            if (!this.grantScopes.account) {
                const accessibleKeys = new Set()
                for (const project of this.accessibleProjects) {
                    for (const env of project.environments?.data ?? []) {
                        accessibleKeys.add(project.key + ':' + env.key)
                    }
                }

                return allRecents.filter((recent) =>
                    accessibleKeys.has(
                        recent.project_key + ':' + recent.environment_key
                    )
                )
            }

            return allRecents
        },
    },
    data() {
        const sessionStore = useSessionStore()

        return {
            future: {
                projectKey: sessionStore.data.current.projectKey,
                environmentKey: sessionStore.data.current.environmentKey,
            },
        }
    },
    watch: {
        // Discard unsaved project/environment picks whenever the panel
        // closes, whether via Switch, the close button, Escape, or an
        // outside click, so the next open always starts from `current`.
        isOpen(isOpen) {
            if (isOpen) return

            this.future = {
                projectKey: this.sessionStore.data.current.projectKey,
                environmentKey: this.sessionStore.data.current.environmentKey,
            }
        },
    },
    methods: {
        selectFavorite(favorite) {
            this.set({
                projectKey: favorite.project_key,
                environmentKey: favorite.environment_key,
            })
        },
        set(future = this.future) {
            this.sessionStore.setCurrent(future).then(() => {
                this.closeMenu()
                this.$emit('changed')
            })
        },
        updateEnvironmentKey() {
            if (this.isSingleEnvironment) {
                this.future.environmentKey =
                    this.futureProject.environments.data[0].key
            }
        },
        openSwap() {
            if (!this.canSwapIntoCurrent) return

            this.swap.open(this.currentEnvironment.key)
            this.closeMenu()
        },
        onSwapPreview(request) {
            this.swap.preview(this.currentProject.key, request)
        },
        onSwapConfirm(request) {
            this.swap.confirm(this.currentProject.key, request)
        },
    },
    mounted() {
        if (
            this.currentProject &&
            this.currentEnvironment &&
            (this.current.projectKey !== this.currentProject.key ||
                this.current.environmentKey !== this.currentEnvironment.key)
        ) {
            this.sessionStore.setCurrent({
                projectKey: this.currentProject.key,
                environmentKey: this.currentEnvironment.key,
                flags: this.current.flags,
            })
        }
    },
}
</script>
